import { Person, Search } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import type { Child } from "../../types";

interface ChildSelectorProps {
  children: Child[];
  selectedChild: string;
  searchTerm: string;
  onSelectChild: (childId: string) => void;
  onSearchTermChange: (term: string) => void;
}

const ChildSelector: React.FC<ChildSelectorProps> = ({
  children,
  selectedChild,
  searchTerm,
  onSelectChild,
  onSearchTermChange,
}) => {
  return (
    <Paper sx={{ p: 4, height: "fit-content" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #892c8b 0%, #a855a7 100%)",
            borderRadius: "50%",
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            boxShadow: "0 4px 12px rgba(137, 44, 139, 0.3)",
          }}
        >
          <Person sx={{ fontSize: 24, color: "white" }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Select Child
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a child to upload photos for
          </Typography>
        </Box>
      </Box>

      {/* Search Box */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&:hover": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              },
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {children.length} child
          {children.length !== 1 ? "ren" : ""} found
        </Typography>
      </Box>

      {/* Children List */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
        >
          Available Children
        </Typography>

        <Box
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "grey.100",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary.main",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            },
          }}
        >
          {children.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No children found
              </Typography>
            </Box>
          ) : (
            children.map((child) => (
              <Card
                key={child.childID}
                sx={{
                  my: 1,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border:
                    selectedChild === child.childID ? "3px solid" : "1px solid",
                  borderColor:
                    selectedChild === child.childID
                      ? "primary.main"
                      : "divider",
                  backgroundColor:
                    selectedChild === child.childID
                      ? "primary.50"
                      : "background.paper",
                  boxShadow:
                    selectedChild === child.childID
                      ? "0 4px 20px rgba(137, 44, 139, 0.25)"
                      : "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      selectedChild === child.childID
                        ? "0 6px 24px rgba(137, 44, 139, 0.35)"
                        : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    borderColor: "primary.main",
                  },
                }}
                onClick={() => onSelectChild(child.childID)}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        background:
                          selectedChild === child.childID
                            ? "linear-gradient(135deg, #892c8b 0%, #a855a7 100%)"
                            : "grey.100",
                        borderRadius: "50%",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Person
                        sx={{
                          fontSize: 20,
                          color:
                            selectedChild === child.childID
                              ? "white"
                              : "text.secondary",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: 40,
                        minWidth: 0,
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color:
                            selectedChild === child.childID
                              ? "primary.main"
                              : "text.primary",
                        }}
                        noWrap
                      >
                        {child.name ||
                          `${child.firstName || ""} ${
                            child.lastName || ""
                          }`.trim()}
                      </Typography>

                      {/* <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={child.class || child.grade || "N/A"}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                        <Chip
                          label={`Age: ${child.age || "N/A"}`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      </Box> */}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ChildSelector;
